import { Link } from "react-router-dom";
import { DisclosureBanner } from "@/components/DisclosureBanner";
import { Landmark, ShieldCheck, Mail, Phone, Clock } from "lucide-react";

const FooterLink = ({ to, children, testid }) => (
  <Link to={to} data-testid={testid} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
    {children}
  </Link>
);

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/info", label: "How It Works" },
  { to: "/membership", label: "Membership" },
  { to: "/membership", label: "Pricing" },
  { to: "/info", label: "FAQ" },
  { to: "/legal/contact-and-company", label: "Contact" },
];

const PRIMARY_LEGAL = [
  { to: "/terms", label: "Terms of Service" },
  { to: "/legal/membership-agreement", label: "Membership Agreement" },
  { to: "/legal/privacy-policy", label: "Privacy Policy" },
  { to: "/legal/refund-cancellation-policy", label: "Refund & Cancellation Policy" },
  { to: "/legal/qsip-disclosure", label: "QSIP Disclosure" },
  { to: "/legal/accessibility-statement", label: "Accessibility" },
  { to: "/legal/privacy-choices", label: "Privacy Choices" },
  { to: "/privacy-request", label: "Privacy Request Form" },
];

const MORE_POLICIES = [
  { to: "/legal/subscription-renewal-terms", label: "Subscription & Renewal Terms" },
  { to: "/legal/legal-advice-disclaimer", label: "Legal Advice Disclaimer" },
  { to: "/legal/financial-disclaimer", label: "Financial Disclaimer" },
  { to: "/legal/upload-data-notice", label: "Upload & Data Processing Notice" },
  { to: "/legal/cookie-policy", label: "Cookie & Tracking Policy" },
  { to: "/legal/data-retention-policy", label: "Data Retention & Deletion" },
  { to: "/legal/age-policy", label: "Age & Eligibility Policy" },
  { to: "/legal/ai-processing-disclosure", label: "AI Processing Disclosure" },
  { to: "/legal/service-description", label: "Report Service Description" },
  { to: "/legal/contact-and-company", label: "Contact & Company Identification" },
  { to: "/legal/ip-notice", label: "Copyright & IP Notice" },
  { to: "/legal", label: "All Policies" },
];

export const SiteFooter = () => (
  <footer className="border-t border-border bg-background" data-testid="site-footer">
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      {/* Brand + main navigation */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-8">
        <div className="space-y-3 max-w-sm">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/15 border border-primary/30">
              <Landmark className="h-4 w-4 text-primary" aria-hidden="true" />
            </span>
            <span className="font-display text-lg font-semibold tracking-tight">GetYourQSIP</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Private Membership Association &middot; document information &amp; report-request portal.
          </p>
          <p className="flex items-center gap-1.5 text-xs text-foreground font-medium" data-testid="footer-no-sell-notice">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            We do not sell your private information.
          </p>
          <div className="space-y-1.5 pt-1" data-testid="footer-contact-block">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Mail className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              <a href="mailto:getyourqsip@gmail.com" className="hover:text-foreground transition-colors" data-testid="footer-support-email">
                getyourqsip@gmail.com
              </a>
            </p>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Phone className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              <a href="tel:+12398887959" className="hover:text-foreground transition-colors" data-testid="footer-support-phone">
                +1 (239) 888-7959
              </a>
            </p>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="footer-support-hours">
              <Clock className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              9:00 A.M.&ndash;5:00 P.M. EST
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
          <nav className="space-y-2" aria-label="Footer navigation" data-testid="footer-nav-links">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground">Explore</p>
            <ul className="space-y-1.5">
              {NAV_LINKS.map((link) => (
                <li key={link.label}>
                  <FooterLink to={link.to} testid={`footer-nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {link.label}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </nav>
          <nav className="space-y-2" aria-label="Legal" data-testid="footer-legal-links">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground">Legal</p>
            <ul className="space-y-1.5">
              {PRIMARY_LEGAL.map((link) => (
                <li key={link.label}>
                  <FooterLink to={link.to} testid={`footer-legal-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {link.label}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </nav>
          <nav className="space-y-2" aria-label="Policies and notices" data-testid="footer-policy-links">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground">Policies &amp; Notices</p>
            <ul className="space-y-1.5">
              {MORE_POLICIES.map((link) => (
                <li key={link.label}>
                  <FooterLink to={link.to} testid={`footer-policy-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {link.label}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Global footer disclaimer (compliance copy) */}
      <p className="text-[11px] text-muted-foreground leading-relaxed border-t border-border pt-6" data-testid="footer-global-disclaimer">
        GetYourQSIP provides private informational research reports. We do not issue or assign official CUSIP
        identifiers and are not affiliated with, sponsored by, endorsed by, or operated by CUSIP Global Services,
        the American Bankers Association, or FactSet Research Systems Inc. Submission of a document does not mean
        that the document or matter has or is eligible for a CUSIP. CUSIP® is a registered trademark of the
        American Bankers Association. Reports are not legal, investment, securities, tax, credit, lending, or
        financial advice and do not guarantee any legal or financial result.
      </p>

      {/* Service disclosure (kept at the bottom of every page) */}
      <DisclosureBanner compact />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <p data-testid="footer-copyright">
          © 2026 UNLIMITED LLC. All rights reserved. GetYourQSIP and related original site content are owned by
          or licensed to UNLIMITED LLC, a Florida limited liability company.
        </p>
        <p>Third-party names, data, and trademarks belong to their respective owners.</p>
      </div>
    </div>
  </footer>
);
